import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertTriangle, Clock, WifiOff } from 'lucide-react';
import api from '../services/api';

/**
 * MaintenanceBanner Component
 * 
 * Shows maintenance mode warnings and countdown.
 * Handles:
 * - WebSocket connection for real-time updates
 * - Countdown display
 * - Force logout for non-admin users
 * - Maintenance mode blocking message
 */
export default function MaintenanceBanner() {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  
  const [maintenanceStatus, setMaintenanceStatus] = useState({
    is_active: false,
    is_countdown_active: false,
    countdown_seconds_remaining: null,
    message: ''
  });
  const [wsConnected, setWsConnected] = useState(false);
  
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const heartbeatIntervalRef = useRef(null);

  // Fetch initial status via REST API
  const fetchStatus = useCallback(async () => {
    try {
      const response = await api.get('/maintenance/status');
      setMaintenanceStatus(response.data);
      
      // If maintenance is active and user is not admin, force logout
      if (response.data.is_active && user && !isAdmin) {
        await logout();
        navigate('/login');
      }
    } catch (err) {
      console.error('[Maintenance] Failed to fetch status:', err);
    }
  }, [user, isAdmin, logout, navigate]);

  // Send heartbeat to track session — only when user is active (not idle)
  const sendHeartbeat = useCallback(async () => {
    if (!user) return;
    // Skip heartbeat if user has been idle for 30 minutes (matches auto-logout threshold)
    const lastActivity = parseInt(localStorage.getItem('lastUserActivity') || '0', 10);
    const idleMs = Date.now() - lastActivity;
    if (lastActivity > 0 && idleMs >= 30 * 60 * 1000) {
      return; // User is idle — don't extend session
    }
    try {
      const currentPage = window.location.pathname;
      await api.post('/maintenance/heartbeat', null, {
        params: { current_page: currentPage }
      });
    } catch (err) {
      // Heartbeat errors are not critical
      console.debug('[Maintenance] Heartbeat failed:', err);
    }
  }, [user]);

  // Connect to WebSocket for real-time updates
  const connectWebSocket = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;
    
    // Get WebSocket URL from current location
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    const wsUrl = `${protocol}//${host}/api/maintenance/ws`;
    
    try {
      wsRef.current = new WebSocket(wsUrl);
      
      wsRef.current.onopen = () => {
        console.log('[Maintenance] WebSocket connected');
        setWsConnected(true);
      };
      
      wsRef.current.onmessage = async (event) => {
        const data = JSON.parse(event.data);
        console.log('[Maintenance] WS message:', data);
        
        switch (data.type) {
          case 'status':
            setMaintenanceStatus(data);
            break;
            
          case 'maintenance_countdown_started':
            setMaintenanceStatus(prev => ({
              ...prev,
              is_countdown_active: true,
              countdown_seconds_remaining: data.countdown_seconds,
              message: data.message
            }));
            break;
            
          case 'countdown_tick':
            setMaintenanceStatus(prev => ({
              ...prev,
              countdown_seconds_remaining: data.seconds_remaining
            }));
            break;
            
          case 'maintenance_activated':
            setMaintenanceStatus(prev => ({
              ...prev,
              is_active: true,
              is_countdown_active: false
            }));
            // Force logout non-admin users
            if (user && !isAdmin) {
              await logout();
              navigate('/login');
            }
            break;
            
          case 'maintenance_ended':
            setMaintenanceStatus({
              is_active: false,
              is_countdown_active: false,
              countdown_seconds_remaining: null,
              message: ''
            });
            break;
            
          default:
            break;
        }
      };
      
      wsRef.current.onclose = () => {
        console.log('[Maintenance] WebSocket closed');
        setWsConnected(false);
        // Attempt reconnect after 5 seconds
        reconnectTimeoutRef.current = setTimeout(connectWebSocket, 5000);
      };
      
      wsRef.current.onerror = (error) => {
        console.error('[Maintenance] WebSocket error:', error);
        wsRef.current?.close();
      };
      
    } catch (err) {
      console.error('[Maintenance] Failed to connect WebSocket:', err);
    }
  }, [user, isAdmin, logout, navigate]);

  // Initial setup
  useEffect(() => {
    // Fetch status on mount
    fetchStatus();
    
    // Connect WebSocket if authenticated
    if (user) {
      connectWebSocket();
      
      // Send initial heartbeat
      sendHeartbeat();
      
      // Setup heartbeat interval (every 30 seconds)
      heartbeatIntervalRef.current = setInterval(sendHeartbeat, 30000);
    }
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
    };
  }, [user, fetchStatus, connectWebSocket, sendHeartbeat]);

  // Poll for status updates as fallback when WS is disconnected
  useEffect(() => {
    if (!wsConnected && user) {
      const pollInterval = setInterval(fetchStatus, 5000);
      return () => clearInterval(pollInterval);
    }
  }, [wsConnected, user, fetchStatus]);

  // Don't show banner if no maintenance activity
  if (!maintenanceStatus.is_active && !maintenanceStatus.is_countdown_active) {
    return null;
  }

  // Show countdown banner
  if (maintenanceStatus.is_countdown_active && maintenanceStatus.countdown_seconds_remaining > 0) {
    return (
      <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-50 max-w-lg w-full px-4" data-testid="maintenance-countdown-banner">
        <Alert variant="destructive" className="bg-amber-50 border-amber-300 text-amber-800 shadow-lg">
          <AlertTriangle className="h-5 w-5 text-amber-600" />
          <AlertTitle className="flex items-center justify-between">
            <span>System Maintenance Warning</span>
            <span className="flex items-center gap-2 text-xl font-bold text-amber-600" data-testid="maintenance-countdown-timer">
              <Clock className="h-5 w-5" />
              {maintenanceStatus.countdown_seconds_remaining}s
            </span>
          </AlertTitle>
          <AlertDescription>
            {maintenanceStatus.message || 'System maintenance will begin shortly. Please save your work.'}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // Show maintenance active banner (for admin users still logged in)
  if (maintenanceStatus.is_active && isAdmin) {
    return (
      <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-50 max-w-lg w-full px-4" data-testid="maintenance-active-banner">
        <Alert variant="destructive" className="bg-red-50 border-red-300 text-red-800 shadow-lg">
          <AlertTriangle className="h-5 w-5 text-red-600" />
          <AlertTitle>Maintenance Mode Active</AlertTitle>
          <AlertDescription>
            Only administrators can access the system. Go to Settings to disable maintenance mode.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return null;
}

/**
 * MaintenanceBlockScreen
 * 
 * Shows a warning on login page when maintenance is active.
 * Does NOT block the form - admins can still log in.
 */
export function MaintenanceBlockScreen() {
  const [status, setStatus] = useState(null);
  
  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const response = await api.get('/maintenance/status');
        setStatus(response.data);
      } catch (err) {
        console.error('Failed to fetch maintenance status:', err);
      }
    };
    
    fetchStatus();
    const interval = setInterval(fetchStatus, 10000);
    return () => clearInterval(interval);
  }, []);
  
  if (!status?.is_active) {
    return null;
  }
  
  // Show as a fixed banner at the top of login page, not blocking
  return (
    <div className="fixed top-0 left-0 right-0 z-50 p-4" data-testid="maintenance-login-warning">
      <Alert variant="destructive" className="max-w-md mx-auto bg-amber-50 border-amber-300 text-amber-800 shadow-lg">
        <AlertTriangle className="h-5 w-5 text-amber-600" />
        <AlertTitle>System Maintenance Active</AlertTitle>
        <AlertDescription>
          Only administrators can log in during maintenance. Non-admin login attempts will be blocked.
        </AlertDescription>
      </Alert>
    </div>
  );
}
